import { useMemo, useState } from 'react';
import { useAppData } from '../store';
import ObligationCard from './ObligationCard';
import type { Obligation } from '../types';
import { CATEGORY_LABELS, type AssetCategory } from '../types';

type FilterKey = 'all' | 'overdue' | 'due_soon' | 'recurring' | 'one_time' | 'inactive';

export default function SearchView() {
  const { obligations, assets, remindersByObligation } = useAppData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [categoryFilter, setCategoryFilter] = useState<AssetCategory | 'all'>('all');
  const [assetFilter, setAssetFilter] = useState<string | 'all'>('all');

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return obligations.filter((o: Obligation) => {
      if (filter === 'inactive' && o.active) return false;
      if (filter !== 'inactive' && !o.active) return false;

      const asset = assets.find((a) => a.id === o.asset_id);

      if (categoryFilter !== 'all' && asset?.category !== categoryFilter) return false;
      if (assetFilter !== 'all' && o.asset_id !== assetFilter) return false;

      if (filter === 'recurring' && o.recurrence_type === 'none') return false;
      if (filter === 'one_time' && o.recurrence_type !== 'none') return false;

      if (filter === 'overdue' && new Date(o.due_date) >= new Date(new Date().toDateString()))
        return false;
      if (filter === 'due_soon') {
        const diffDays =
          (new Date(o.due_date).getTime() - new Date(new Date().toDateString()).getTime()) /
          86400000;
        if (diffDays < 0 || diffDays > 30) return false;
      }

      if (!q) return true;
      const haystack = [o.name, o.description, o.notes, o.payee, asset?.name, asset?.category]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [obligations, assets, query, filter, categoryFilter, assetFilter]);

  return (
    <div className="search-view">
      <h1>Search</h1>
      <input
        className="search-input"
        type="search"
        placeholder="insurance, boat, registration…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />

      <div className="filter-row">
        <select value={filter} onChange={(e) => setFilter(e.target.value as FilterKey)}>
          <option value="all">All active</option>
          <option value="overdue">Overdue</option>
          <option value="due_soon">Due soon</option>
          <option value="recurring">Recurring</option>
          <option value="one_time">One-time</option>
          <option value="inactive">Completed / inactive</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as AssetCategory | 'all')}
        >
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <select value={assetFilter} onChange={(e) => setAssetFilter(e.target.value)}>
          <option value="all">All assets</option>
          {assets.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      <p className="muted">
        {results.length} result{results.length === 1 ? '' : 's'}
      </p>

      <div className="card-list">
        {results.map((o) => (
          <ObligationCard key={o.id} obligation={o} reminders={remindersByObligation[o.id]} />
        ))}
      </div>
    </div>
  );
}
